import { useEffect, useMemo, useRef, useState } from 'react'
import {
  apercuDernier,
  conversationsAffichees,
  envoyerMessageClient,
  envoyerMessageCourse,
  lireFilClient,
  lireFilsCourses,
  marquerFilClientLu,
  whatsappClient,
  type ConversationClient,
  type MessageClient,
  type MessageCourse,
} from '../../api/messagerieClients'
import { initiales } from '../../api/operations'
import type { Client, Course, DonneesOps } from '../../api/types'
import { useDroits } from '../Contexte'
import { Icone } from '../Icone'
import { dateCourte, estAujourdHui, heure } from '../Format'

/** « Aujourd'hui » / « Hier » / « Mardi 23 septembre ». */
function libelleJour(iso: string): string {
  const d = new Date(iso)
  const maintenant = new Date()
  if (d.toDateString() === maintenant.toDateString()) return "Aujourd'hui"
  if (d.toDateString() === new Date(maintenant.getTime() - 86_400_000).toDateString()) return 'Hier'
  const s = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const quand = (iso: string) => (estAujourdHui(iso) ? heure(iso) : dateCourte(iso).slice(0, 5))
const EN_COURS = ['assignee', 'collecte', 'en_livraison']

/* ================================================================== fil d'assistance client ↔ équipe */

/**
 * Le fil d'assistance d'un client : une conversation par client, une course en contexte ou non. Le client la lit dans
 * l'app (compte relié à sa fiche) ; le livreur jamais. Sans compte, on n'écrit pas dans le vide : WhatsApp.
 */
export function FilClients({
  donnees,
  surRafraichir,
  surNotifier,
  clientInitial,
  courseInitiale,
}: {
  donnees: DonneesOps
  surRafraichir: () => Promise<void>
  surNotifier: (m: string) => void
  clientInitial?: string | null
  courseInitiale?: string | null
}) {
  const { apercu, fixturesMessagerie } = useDroits()
  const [selection, setSelection] = useState<string | null>(clientInitial ?? null)
  const [recherche, setRecherche] = useState('')
  const [fil, setFil] = useState<MessageClient[]>([])
  const [chargement, setChargement] = useState(false)
  const [brouillon, setBrouillon] = useState('')
  const [contexte, setContexte] = useState<string>(courseInitiale ?? '')
  const [envoi, setEnvoi] = useState(false)
  const filRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (clientInitial) setSelection(clientInitial)
    if (courseInitiale) setContexte(courseInitiale)
  }, [clientInitial, courseInitiale])

  /* En aperçu : conversations calculées à partir des messages fictifs, envois gardés en mémoire. */
  const [locaux, setLocaux] = useState<MessageClient[]>([])
  const conversations: ConversationClient[] = useMemo(() => {
    if (!apercu) return donnees.conversationsClients
    const tous = [...(fixturesMessagerie?.messagesClients ?? []), ...locaux]
    const parClient = new Map<string, MessageClient[]>()
    for (const m of tous) parClient.set(m.client_account_id, [...(parClient.get(m.client_account_id) ?? []), m])
    return [...parClient.entries()].map(([id, ms]) => {
      const c = donnees.tousClients.find((x) => x.id === id)
      const d = ms[ms.length - 1]!
      return {
        client_account_id: id, nom: c?.name ?? 'Client', type: c?.type ?? 'particulier', telephone: c?.phone ?? null,
        a_un_compte: c?.a_un_compte ?? false, dernier_corps: d.corps, dernier_auteur: d.auteur, dernier_le: d.created_at,
        dernier_course_ref: donnees.courses.find((x) => x.id === d.course_id)?.ref ?? null,
        non_lus: ms.filter((m) => m.auteur === 'client' && !m.lu_le).length, total: ms.length,
      }
    })
  }, [apercu, donnees, fixturesMessagerie, locaux])

  const { existantes, nouvelles } = conversationsAffichees(conversations, donnees.clients, recherche)
  const conv = conversations.find((c) => c.client_account_id === selection) ?? null
  const fiche: Client | null = donnees.tousClients.find((c) => c.id === selection) ?? null
  const total = conv?.total ?? 0

  /* Le fil s'ouvre, puis se relit quand un message arrive (le total de la conversation change). */
  useEffect(() => {
    if (!selection) return
    if (apercu) {
      setFil([...(fixturesMessagerie?.messagesClients ?? []), ...locaux].filter((m) => m.client_account_id === selection))
      return
    }
    let vivant = true
    setChargement(true)
    lireFilClient(selection)
      .then((ms) => vivant && setFil(ms))
      .catch((e: unknown) => vivant && surNotifier(e instanceof Error ? e.message : String(e)))
      .finally(() => vivant && setChargement(false))
    return () => {
      vivant = false
    }
  }, [selection, total, apercu, locaux])

  /* Accusé de lecture : à l'ouverture, et à chaque nouveau message du client pendant que le fil est ouvert. */
  useEffect(() => {
    if (!selection || apercu || !conv || conv.non_lus === 0) return
    marquerFilClientLu(selection).then(() => surRafraichir()).catch(() => {})
  }, [selection, conv?.non_lus])

  useEffect(() => {
    const el = filRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [fil])

  /* Les courses de ce client qu'on peut citer : en cours d'abord, puis les plus récentes. */
  const coursesClient = useMemo(() => {
    if (!selection) return []
    const toutes = [...donnees.courses, ...donnees.livrees].filter((c) => c.client_account_id === selection)
    const uniques = [...new Map(toutes.map((c) => [c.id, c])).values()]
    return uniques
      .sort((a, b) => Number(EN_COURS.includes(b.statut)) - Number(EN_COURS.includes(a.statut)) || b.created_at.localeCompare(a.created_at))
      .slice(0, 15)
  }, [selection, donnees.courses, donnees.livrees])
  /* Contexte proposé à l'ouverture d'un fil : la course demandée (depuis le détail d'une course), sinon la seule course
     en cours du client s'il n'en a qu'une, sinon aucune. */
  useEffect(() => {
    if (!selection) return
    if (courseInitiale && selection === clientInitial) {
      setContexte(courseInitiale)
      return
    }
    const enCours = coursesClient.filter((c) => EN_COURS.includes(c.statut))
    setContexte(enCours.length === 1 ? enCours[0]!.id : '')
  }, [selection])
  const refCourse = (id: string | null) => (id ? [...donnees.courses, ...donnees.livrees].find((c) => c.id === id)?.ref ?? null : null)

  async function envoyer() {
    const texte = brouillon.trim()
    if (!texte || !selection || envoi) return
    setEnvoi(true)
    setBrouillon('')
    try {
      if (apercu) {
        setLocaux((l) => [...l, {
          id: `local-${Date.now()}`, client_account_id: selection, course_id: contexte || null, auteur: 'equipe', source: 'console',
          corps: texte, lu_le: null, created_at: new Date().toISOString(),
        }])
      } else {
        await envoyerMessageClient(selection, texte, contexte || null)
        await surRafraichir()
      }
    } catch (e) {
      surNotifier(e instanceof Error ? e.message : String(e))
      setBrouillon(texte)
    } finally {
      setEnvoi(false)
    }
  }

  const aUnCompte = conv?.a_un_compte ?? fiche?.a_un_compte ?? false
  const telephone = conv?.telephone ?? fiche?.phone ?? null
  const nom = conv?.nom ?? fiche?.name ?? ''
  const wa = whatsappClient(telephone, nom ? `Bonjour ${nom.split(' ')[0]}, ` : '')

  let dernierJour = ''
  return (
    <div className="msg-cols">
      <div className="msg-side">
        <div className="ms-head">Conversations</div>
        <div className="ms-recherche">
          <input type="search" id="recherche-conversation" placeholder="Chercher ou écrire à un client" value={recherche}
            onChange={(e) => setRecherche(e.target.value)} aria-label="Chercher un client" autoComplete="off" />
        </div>
        <div>
          {existantes.length === 0 && nouvelles.length === 0 && (
            <div className="msg-placeholder" style={{ padding: 22 }}>
              {recherche ? 'Aucun client ne correspond.' : 'Aucune conversation pour l’instant. Cherchez un client pour lui écrire.'}
            </div>
          )}
          {existantes.map((c) => (
            <div key={c.client_account_id} className={`ms-item${selection === c.client_account_id ? ' sel' : ''}`}
              onClick={() => setSelection(c.client_account_id)} role="button" tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setSelection(c.client_account_id)}>
              <span className="avatar">{initiales(c.nom)}</span>
              <div className="mi-body">
                <div className="mi-name">{c.nom}</div>
                <div className="mi-last">{apercuDernier(c)}</div>
              </div>
              <div className="mi-meta">
                <span className="mi-time">{quand(c.dernier_le)}</span>
                {c.non_lus > 0 && <span className="mi-unread">{c.non_lus}</span>}
              </div>
            </div>
          ))}
          {nouvelles.length > 0 && <div className="ms-head ms-sous">Nouvelle conversation</div>}
          {nouvelles.map((c) => (
            <div key={c.id} className={`ms-item${selection === c.id ? ' sel' : ''}`} onClick={() => { setSelection(c.id); setRecherche('') }}
              role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setSelection(c.id)}>
              <span className="avatar">{initiales(c.name)}</span>
              <div className="mi-body">
                <div className="mi-name">{c.name}</div>
                <div className="mi-last">{c.a_un_compte ? 'Compte dans l’app' : 'Sans compte : WhatsApp'}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="msg-main">
        {!selection ? (
          <div className="msg-placeholder">
            <Icone nom="chat" />
            <div className="mp-t">Messagerie clients</div>
            Choisissez une conversation, ou cherchez un client pour lui écrire. Le livreur ne voit jamais ces échanges.
          </div>
        ) : (
          <>
            <div className="mt-head">
              <span className="avatar">{initiales(nom)}</span>
              <div style={{ minWidth: 0 }}>
                <div className="th-name">{nom}</div>
                <div className="th-sub">
                  {(conv?.type ?? fiche?.type) === 'pme' ? 'PME' : 'Particulier'}
                  {telephone ? ` · ${telephone}` : ''} · {aUnCompte ? 'lit ce fil dans l’app' : 'sans compte dans l’app'}
                </div>
              </div>
              <div style={{ flex: 1 }} />
              {wa && (
                <a className="btn btn-outline btn-sm" href={wa} target="_blank" rel="noreferrer">
                  <Icone nom="phone" /> WhatsApp
                </a>
              )}
            </div>
            <div className="msg-thread" ref={filRef} aria-busy={chargement}>
              {fil.length === 0 ? (
                <div className="msg-placeholder">
                  {chargement ? 'Chargement…' : aUnCompte ? `Aucun message avec ${nom}. Écrivez le premier.` : `Aucun message avec ${nom}.`}
                </div>
              ) : (
                fil.map((m) => {
                  const jour = libelleJour(m.created_at)
                  const nouveau = jour !== dernierJour
                  dernierJour = jour
                  const ref = refCourse(m.course_id)
                  return (
                    <div key={m.id} style={{ display: 'contents' }}>
                      {nouveau && <div className="msg-day">{jour}</div>}
                      <div className={`msg-b from-${m.auteur === 'client' ? 'driver' : 'dispatch'}`}>
                        {ref && <div className="msg-contexte">À propos de {ref}</div>}
                        {m.corps}
                        <div className="mb-time">
                          {heure(m.created_at)}
                          {m.auteur === 'equipe' && m.lu_le ? ' · lu' : ''}
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
            {aUnCompte ? (
              <div className="msg-compose msg-compose-contexte">
                <select id="contexte-course" value={contexte} onChange={(e) => setContexte(e.target.value)} aria-label="Course concernée">
                  <option value="">Sans course</option>
                  {coursesClient.map((c) => (
                    <option key={c.id} value={c.id}>{c.ref}{EN_COURS.includes(c.statut) ? ' (en cours)' : ''}</option>
                  ))}
                </select>
                <input id="brouillon-client" placeholder={`Écrire à ${nom}…`} maxLength={2000} autoComplete="off" value={brouillon}
                  onChange={(e) => setBrouillon(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void envoyer() }} />
                <button className="msg-send" onClick={() => void envoyer()} aria-label="Envoyer" title="Envoyer" disabled={envoi || !brouillon.trim()}>
                  <Icone nom="send" />
                </button>
              </div>
            ) : (
              <div className="msg-compose msg-sans-compte">
                <span>{nom} n’a pas de compte dans l’app : il ne lirait pas ce fil.{wa ? ' Écrivez-lui sur WhatsApp.' : ' Aucun numéro utilisable sur sa fiche.'}</span>
                {wa && (
                  <a className="btn btn-sm" href={wa} target="_blank" rel="noreferrer">
                    <Icone nom="phone" /> Ouvrir WhatsApp
                  </a>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/* ================================================================== fils client ↔ livreur des courses en cours */

const AUTEUR: Record<MessageCourse['auteur'], string> = { client: 'Client', livreur: 'Livreur', equipe: 'Vous' }

/**
 * Les courses de l'app en cours ont un fil client ↔ livreur (0047). L'équipe le lit et peut y écrire (0067) pour
 * débloquer une remise. Il se ferme à la fin de la course.
 */
export function FilCourses({
  donnees,
  surNotifier,
}: {
  donnees: DonneesOps
  surNotifier: (m: string) => void
}) {
  const { apercu, fixturesMessagerie } = useDroits()
  const courses: Course[] = donnees.courses
    .filter((c) => c.app_particulier && EN_COURS.includes(c.statut))
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
  const ids = courses.map((c) => c.id).join(',')
  const [messages, setMessages] = useState<MessageCourse[]>([])
  const [selection, setSelection] = useState<string | null>(null)
  const [brouillon, setBrouillon] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const filRef = useRef<HTMLDivElement>(null)

  /* Rechargés à chaque rechargement de l'état (le temps réel recharge l'état à chaque message). */
  useEffect(() => {
    if (apercu) {
      setMessages((m) => {
        const base = (fixturesMessagerie?.messagesCourses ?? []).filter((x) => ids.includes(x.course_id))
        const locaux = m.filter((x) => x.id.startsWith('local-'))
        return [...base, ...locaux]
      })
      return
    }
    let vivant = true
    void lireFilsCourses(ids ? ids.split(',') : []).then((ms) => vivant && setMessages(ms))
    return () => {
      vivant = false
    }
  }, [ids, donnees, apercu])

  useEffect(() => {
    const el = filRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, selection])

  const course = courses.find((c) => c.id === selection) ?? null
  const fil = messages.filter((m) => m.course_id === selection)
  const client = course ? donnees.tousClients.find((c) => c.id === course.client_account_id) : null
  const livreur = course ? donnees.drivers.find((d) => d.id === course.driver_id) : null

  async function envoyer() {
    const texte = brouillon.trim()
    if (!texte || !selection || envoi) return
    setEnvoi(true)
    setBrouillon('')
    try {
      if (apercu) {
        setMessages((m) => [...m, { id: `local-${Date.now()}`, course_id: selection, auteur: 'equipe', corps: texte, lu_le: null, created_at: new Date().toISOString() }])
      } else {
        await envoyerMessageCourse(selection, texte)
        setMessages(await lireFilsCourses(courses.map((c) => c.id)))
      }
    } catch (e) {
      surNotifier(e instanceof Error ? e.message : String(e))
      setBrouillon(texte)
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <div className="msg-cols">
      <div className="msg-side">
        <div className="ms-head">{courses.length} course{courses.length > 1 ? 's' : ''} de l’app en cours</div>
        <div>
          {courses.length === 0 ? (
            <div className="msg-placeholder" style={{ padding: 22 }}>
              Aucune course de l’app en cours. Pendant une course de l’app, le client et le livreur échangent ici, et vous pouvez intervenir.
            </div>
          ) : (
            courses.map((c) => {
              const ms = messages.filter((m) => m.course_id === c.id)
              const dernier = ms[ms.length - 1]
              const nomClient = donnees.tousClients.find((x) => x.id === c.client_account_id)?.name ?? 'Client'
              return (
                <div key={c.id} className={`ms-item${selection === c.id ? ' sel' : ''}`} onClick={() => setSelection(c.id)}
                  role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setSelection(c.id)}>
                  <span className="avatar">{initiales(nomClient)}</span>
                  <div className="mi-body">
                    <div className="mi-name">{c.ref} · {nomClient}</div>
                    <div className="mi-last">{dernier ? `${AUTEUR[dernier.auteur]} : ${dernier.corps}` : 'Pas encore de message'}</div>
                  </div>
                  {dernier && <div className="mi-meta"><span className="mi-time">{quand(dernier.created_at)}</span></div>}
                </div>
              )
            })
          )}
        </div>
      </div>

      <div className="msg-main">
        {!course ? (
          <div className="msg-placeholder">
            <Icone nom="dispatch" />
            <div className="mp-t">Fils des courses</div>
            Choisissez une course pour lire l’échange entre le client et le livreur, et y répondre si besoin.
          </div>
        ) : (
          <>
            <div className="mt-head">
              <span className="avatar">{initiales(client?.name ?? 'C')}</span>
              <div>
                <div className="th-name">{course.ref} · {client?.name ?? 'Client'}</div>
                <div className="th-sub">Livreur : {livreur?.full_name ?? 'non assigné'} · {course.dropoff || 'adresse à venir'}</div>
              </div>
            </div>
            <div className="msg-thread" ref={filRef}>
              {fil.length === 0 ? (
                <div className="msg-placeholder">Pas encore de message dans cette course.</div>
              ) : (
                fil.map((m) => (
                  <div key={m.id} className={`msg-b from-${m.auteur === 'equipe' ? 'dispatch' : 'driver'}${m.auteur === 'livreur' ? ' de-livreur' : ''}`}>
                    <div className="msg-auteur">{m.auteur === 'equipe' ? 'Vous' : m.auteur === 'livreur' ? livreur?.full_name ?? 'Livreur' : client?.name ?? 'Client'}</div>
                    {m.corps}
                    <div className="mb-time">{heure(m.created_at)}</div>
                  </div>
                ))
              )}
            </div>
            <div className="msg-compose">
              <input id="brouillon-course" placeholder="Écrire au client et au livreur…" maxLength={1000} autoComplete="off" value={brouillon}
                onChange={(e) => setBrouillon(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void envoyer() }} />
              <button className="msg-send" onClick={() => void envoyer()} aria-label="Envoyer" title="Envoyer" disabled={envoi || !brouillon.trim()}>
                <Icone nom="send" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
